import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';

import { debounceTime } from 'rxjs';

import { Store } from '@ngrx/store';

import { selectChannels, selectUser } from '../../../../../store/app.selectors';
import { SnackBarService } from '../../../../../core/services/snackbar.service';
import { IUserProfile } from '../../../../../modules/user/interfaces/IUserProfile';
import { ChannelRoles, ChannelType, IChannel, IChannelCreationDTO } from '../../../interfaces/IChannel';
import { IMessageableEntity } from '../../../interfaces/IMessageableEntity';
import { MessagingApiService } from '../../../services/messaging-api/messaging-api.service';
import { selectChannel, sendMessage, setNewChannel, toggleSidebar } from '../../../store/chat.actions';
import { AppStateWithChat } from '../../../store/chat.reducer';

@Component({
  selector: 'ngx-new-direct-chat',
  templateUrl: './new-direct-chat.component.html',
  styleUrls: ['./new-direct-chat.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class NewDirectChatComponent implements OnInit {
  private fb = inject(FormBuilder);
  private store = inject(Store<AppStateWithChat>);
  private dialogRef = inject(MatDialogRef<NewDirectChatComponent>);
  private dataService = inject(MessagingApiService);
  private snackBarService = inject(SnackBarService);

  user = toSignal(this.store.select(selectUser), { initialValue: null as IUserProfile | null });
  channels = toSignal(this.store.select(selectChannels), { initialValue: [] as IChannel[] });

  users = signal<IMessageableEntity[]>([]);
  loadingUsers = signal(false);
  pageUsers = signal(1);
  searchTermUsers = signal('');
  hasMoreUsers = signal(true);
  isLoading = signal(false);
  private readonly usersPageSize = 20;
  private usersRequest = 0;

  newChatForm: FormGroup = this.fb.group({
    availableUser: [null, [Validators.required]],
    description: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(250)]],
  });

  ngOnInit(): void {
    this.searchUsers();
    this.newChatForm
      .get('availableUser')!
      .valueChanges.pipe(debounceTime(500))
      .subscribe((data: string) => {
        if (typeof data !== 'string') {
          return;
        }
        this.usersRequest += 1;
        this.loadingUsers.set(false);
        this.pageUsers.set(1);
        this.hasMoreUsers.set(true);
        this.searchTermUsers.set(data);
        this.users.set([]);
        this.searchUsers();
      });
  }

  searchUsers(): void {
    if (this.loadingUsers() || !this.hasMoreUsers()) {
      return;
    }
    this.loadingUsers.set(true);
    const request = this.usersRequest;
    this.dataService
      .searchUsers({ searchTerm: this.searchTermUsers(), page: this.pageUsers(), size: this.usersPageSize })
      .subscribe({
        next: (data) => {
          if (request !== this.usersRequest) {
            return;
          }
          this.users.update((users) => [...users, ...data]);
          this.hasMoreUsers.set(data.length >= this.usersPageSize);
          this.loadingUsers.set(false);
        },
        error: () => {
          if (request !== this.usersRequest) {
            return;
          }
          this.loadingUsers.set(false);
        },
      });
  }

  close(): void {
    this.dialogRef.close();
  }

  getOptionText(option: IMessageableEntity): string {
    return option?.displayName;
  }

  sendMessage(): void {
    const user = this.user();
    if (!user) {
      return;
    }
    this.isLoading.set(true);
    const channel: IChannelCreationDTO = {
      displayName: (this.newChatForm.value.availableUser as IMessageableEntity).displayName,
      role: ChannelRoles.ADMIN,
      type: ChannelType.DIRECT_MESSAGE,
      isPrivate: true,
    };
    this.store.dispatch(toggleSidebar({ active: false }));
    this.dataService
      .createChannel(channel, [
        (this.newChatForm.value.availableUser as IMessageableEntity).uid,
        user.subjectMessageableEntity.uid,
      ])
      .subscribe({
        next: (newChannel) => {
          const optionalChannel = this.channels().find((ch) => ch.uid === newChannel.uid);
          const channelToSelect = optionalChannel ?? newChannel;
          if (!optionalChannel) {
            this.store.dispatch(setNewChannel({ channel: newChannel }));
          }
          this.store.dispatch(selectChannel({ channel: channelToSelect }));
          this.store.dispatch(
            sendMessage({
              message: this.newChatForm.value.description as string,
              attachments: [],
            })
          );
          this.close();
        },
        error: () => {
          this.isLoading.set(false);
          this.snackBarService.showErrorSnackBar();
        },
      });
  }

  onScroll(): void {
    if (this.loadingUsers() || !this.hasMoreUsers()) {
      return;
    }
    this.pageUsers.update((page) => page + 1);
    this.searchUsers();
  }
}
